<template>
  <div class="m-selector-wrapper">
    <Select
      :inputId="uniqueId"
      ref="selectRef"
      v-model="selectedValue"
      :options="items"
      :optionLabel="itemText"
      :optionValue="itemValue"
      :optionDisabled="itemDisabled"
      :filter="filterable"
      :placeholder="effectivePlaceholder"
      :class="selectorClasses"
      :overlay-class="overlayClass"
      :pt="selectPassThrough"
      v-bind="filteredProps"
      appendTo="#ui-popup-root"
      @change="handleChange"
      @update:modelValue="handleModelValueUpdate"
      @option-select="handleOptionSelect"
      @before-show="onOverlayBeforeShow"
      @show="onOverlayShow"
      @blur="handleBlur"
    >
      <template #value="slotProps">
        <div v-if="multiple && slotProps.value && slotProps.value.length" class="selected-values">
          <span v-for="(item, index) in slotProps.value" :key="index" class="selected-tag">
            {{ getItemLabel(item) }}
            <span v-if="Number(index) < slotProps.value.length - 1">, </span>
          </span>
        </div>
        <span
          v-else-if="!multiple"
          class="m-selector-value"
          :class="{ 'm-selector-value--placeholder': isEmptySelectValue(slotProps.value) }"
        >
          {{ getSingleSelectDisplayText(slotProps.value) }}
        </span>
      </template>

      <template #option="slotProps">
        <div class="selector-option">
          {{ getOptionDisplayLabel(slotProps.option) }}
        </div>
      </template>
    </Select>

    <div v-if="hint" class="m-selector-hint">
      {{ hint }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { buildStableTestId } from '@/common/utils/testId'
import { useI18n } from '@/common/composables/useI18n'
import {
  isRepeatedListboxSelection,
  resolvePrimeSelectEventValue,
} from '@/common/utils/elementOnChangeSelection'
import Select from 'primevue/select'
import { computed, nextTick, onMounted, ref, useAttrs, useId, watch } from 'vue'

interface Props {
  modelValue?: any
  items?: any[]
  itemValue?: string
  itemText?: string
  itemDisabled?: (item: any) => boolean
  placeholder?: string
  label?: string
  disabled?: boolean
  readonly?: boolean
  loading?: boolean
  multiple?: boolean
  returnObject?: boolean
  clearable?: boolean
  filterable?: boolean
  align?: 'left' | 'center' | 'right'
  hint?: string
  /** id элемента — для data-testid */
  elementId?: string | number | null
  /** Fallback подписи, если value не найден в items (ListBox __listbox / __text). */
  resolveItemLabel?: (value: unknown) => string | undefined
  /** Автовыбор первого пункта при пустом modelValue (формы). Выкл. для table editor. */
  autoSelectFirst?: boolean
  /** Компактная высота (ячейки таблицы). */
  compact?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  items: () => [],
  itemValue: 'value',
  itemText: 'text',
  itemDisabled: () => false,
  placeholder: undefined,
  label: '',
  disabled: false,
  readonly: false,
  loading: false,
  multiple: false,
  returnObject: false,
  // Swing ListBox has no clear (X); enable only when caller passes clearable explicitly.
  clearable: false,
  filterable: false,
  align: 'left',
  hint: '',
  elementId: null,
  resolveItemLabel: undefined,
  autoSelectFirst: true,
  compact: false,
})

const emit = defineEmits<{
  'update:modelValue': [value: any]
  change: [value: any]
  'user-change': [value: any]
  blur: []
}>()

const attrs = useAttrs()
const { t } = useI18n()
const selectRef = ref()
const uniqueId = useId()

const effectivePlaceholder = computed(() => props.placeholder ?? t('ui.selectValue'))

const overlayClass = computed(() =>
  ['m-selector-overlay', props.compact ? 'm-selector-overlay--compact' : ''].filter(Boolean).join(' '),
)

const selectorListTestId = computed(() => {
  const id = props.elementId
  if (id == null || String(id).trim() === '') return undefined
  return buildStableTestId('element', [id, 'listbox'])
})

const selectPassThrough = computed(() => {
  const listTestId = selectorListTestId.value
  if (!listTestId) {
    return undefined
  }

  return {
    listContainer: { 'data-testid': listTestId },
  }
})

/** Non-editable ListBox: block open + hide chevron (Swing read-only look). */
const isNonEditable = computed(() => Boolean(props.disabled || props.readonly))

const filteredProps = computed(() => {
  const {
    modelValue,
    items,
    itemValue,
    itemText,
    itemDisabled,
    label,
    placeholder,
    hint,
    elementId,
    resolveItemLabel,
    autoSelectFirst,
    compact,
    readonly: _readonly,
    disabled: _disabled,
    clearable,
    ...rest
  } = props
  return {
    ...rest,
    ...attrs,
    disabled: isNonEditable.value,
    showClear: !isNonEditable.value && clearable === true,
  }
})

const OVERLAY_ANCHOR_WIDTH_VAR = '--m-selector-overlay-anchor-width'

/**
 * PrimeVue measures overlay geometry before @show. Long option text inflates the
 * panel, and absolutePosition places that wide box off to the side. Lock the
 * anchor width first: a reactive overlayStyle would wipe top/left.
 */
const publishOverlayAnchorWidth = (): number => {
  const selectEl = (selectRef.value as { $el?: HTMLElement } | null)?.$el
  const width = selectEl?.offsetWidth ?? 0
  if (!width) {
    document.documentElement.style.removeProperty(OVERLAY_ANCHOR_WIDTH_VAR)
    return 0
  }
  document.documentElement.style.setProperty(OVERLAY_ANCHOR_WIDTH_VAR, `${width}px`)
  return width
}

const constrainOverlayToAnchor = () => {
  const width = publishOverlayAnchorWidth()
  if (!width) return

  const inst = selectRef.value as {
    overlay?: HTMLElement | null
    alignOverlay?: () => void
  } | null
  let overlay = inst?.overlay ?? null
  if (!overlay || !overlay.isConnected) {
    const overlays = document.querySelectorAll<HTMLElement>('.p-select-overlay.m-selector-overlay')
    overlay = overlays[overlays.length - 1] ?? null
  }
  if (!overlay) return

  overlay.style.width = `${width}px`
  overlay.style.maxWidth = `${width}px`
  inst?.alignOverlay?.()
}

const onOverlayBeforeShow = () => {
  publishOverlayAnchorWidth()
  requestAnimationFrame(() => {
    constrainOverlayToAnchor()
  })
}

const onOverlayShow = () => {
  selectionFromOverlay = true
  void nextTick(() => {
    constrainOverlayToAnchor()
  })
}

const selectedValue = computed({
  get: () => props.modelValue,
  set: (value) => {
    emit('update:modelValue', value)
  },
})

let applyingDefaultSelection = false
let selectionFromOverlay = false

const emitUserSelection = (value: unknown) => {
  if (applyingDefaultSelection) return
  if (isRepeatedListboxSelection(value, props.modelValue)) return
  emit('change', value)
  emit('user-change', value)
}

const handleChange = (event: any) => {
  const value = resolvePrimeSelectEventValue(event)
  emitUserSelection(value)
  // Close the dropdown after selection
  setTimeout(() => {
    if (selectRef.value && selectRef.value.hide) {
      selectRef.value.hide()
    }
  }, 0)
}

const handleModelValueUpdate = (value: any) => {
  const next = resolvePrimeSelectEventValue(value)
  if (selectionFromOverlay && next !== props.modelValue) {
    emitUserSelection(next)
    selectionFromOverlay = false
  }
  setTimeout(() => {
    if (selectRef.value && selectRef.value.hide) {
      selectRef.value.hide()
    }
  }, 0)
}

const handleOptionSelect = (event: any) => {
  emitUserSelection(resolvePrimeSelectEventValue(event))
  // Close dropdown when option is selected
  setTimeout(() => {
    if (selectRef.value && selectRef.value.hide) {
      selectRef.value.hide()
    }
    // Also try to blur the element
    if (selectRef.value && selectRef.value.$el) {
      selectRef.value.$el.blur()
    }
  }, 10)
}

const handleBlur = () => {
  emit('blur')
}

const selectorClasses = computed(() => [
  'm-selector',
  `text-align-${props.align}`,
  {
    'm-selector--multiple': props.multiple,
    'm-selector--disabled': isNonEditable.value,
    'm-selector--readonly': isNonEditable.value,
    'm-selector--compact': props.compact,
  },
])

const isEmptySelectValue = (value: unknown): boolean => value == null || value === ''

const getSingleSelectDisplayText = (value: unknown): string => {
  if (isEmptySelectValue(value)) {
    return props.placeholder ?? ''
  }
  return getItemLabel(value)
}

const getOptionDisplayLabel = (option: any): string => {
  if (option == null) return ''
  if (typeof option === 'object' && !Array.isArray(option)) {
    const fromItemText = props.itemText ? option[props.itemText] : undefined
    if (fromItemText !== undefined) return String(fromItemText)
    if (option.text !== undefined) return String(option.text)
    if (option.label !== undefined) return String(option.label)
    return ''
  }
  return getItemLabel(option)
}

const getItemLabel = (item: any) => {
  if (item === null || item === undefined) {
    return ''
  }

  if (typeof item === 'object' && !Array.isArray(item)) {
    if (props.itemText && item[props.itemText] !== undefined) {
      return String(item[props.itemText])
    }
    if (item.text !== undefined) {
      return String(item.text)
    }
    if (item.label !== undefined) {
      return String(item.label)
    }
    if (item.toString && item.toString() !== '[object Object]') {
      return item.toString()
    }
    return ''
  }

  const foundOption = props.items?.find((option) => {
    if (typeof option === 'object' && option !== null && !Array.isArray(option)) {
      const optionValue = option[props.itemValue]
      return optionValue === item || String(optionValue) === String(item)
    }
    return option === item || String(option) === String(item)
  })

  if (foundOption) {
    if (typeof foundOption === 'object' && !Array.isArray(foundOption)) {
      const fromItemText = foundOption[props.itemText]
      if (fromItemText !== undefined) {
        return String(fromItemText)
      }
      if (foundOption.text !== undefined) {
        return String(foundOption.text)
      }
      if (foundOption.label !== undefined) {
        return String(foundOption.label)
      }
    }
  }

  if (props.resolveItemLabel) {
    const custom = props.resolveItemLabel(item)
    if (custom !== undefined) {
      return custom
    }
  }

  return String(item)
}

const getFirstItemValue = () => {
  const firstItem = props.items?.[0]
  if (firstItem == null) return null
  if (props.returnObject) return firstItem
  if (typeof firstItem === 'object' && !Array.isArray(firstItem)) {
    return firstItem[props.itemValue]
  }
  return firstItem
}

const applyDefaultSelectionIfNeeded = () => {
  if (!props.autoSelectFirst) return
  if (props.multiple) return
  if (props.modelValue !== null && props.modelValue !== undefined) return
  if (!Array.isArray(props.items) || props.items.length === 0) return

  const nextValue = getFirstItemValue()
  if (nextValue === null || nextValue === undefined) return

  applyingDefaultSelection = true
  emit('update:modelValue', nextValue)
  void nextTick(() => {
    applyingDefaultSelection = false
  })
}

onMounted(() => {
  applyDefaultSelectionIfNeeded()
})

watch(
  () => [props.items, props.modelValue],
  () => {
    applyDefaultSelectionIfNeeded()
  },
  { deep: true },
)
</script>

<style lang="scss">
.m-selector-wrapper {
  position: relative;
  width: 100%;

  // Global override for this component to ensure light theme
  --p-accordion-content-background: #ffffff !important;
  --p-content-background: #ffffff !important;
  --p-surface-0: #ffffff !important;
  --p-surface-50: #ffffff !important;
  --p-surface-100: #ffffff !important;

  // Force light theme for all child elements
  * {
    --p-accordion-content-background: #ffffff !important;
    --p-content-background: #ffffff !important;
    --p-surface-0: #ffffff !important;
    --p-surface-50: #ffffff !important;
    --p-surface-100: #ffffff !important;
  }
}

// Global overrides for PrimeVue Select components
.p-select-overlay,
.p-select-list-container,
.p-select-panel,
.p-component-overlay,
.p-overlay,
.p-connected-overlay {
  --p-accordion-content-background: #ffffff !important;
  --p-content-background: #ffffff !important;
  --p-surface-0: #ffffff !important;
  --p-surface-50: #ffffff !important;
  --p-surface-100: #ffffff !important;
  background: #ffffff !important;
  background-color: #ffffff !important;
  border: 1px solid #d1d5db !important;

  .p-select-panel,
  .p-select-list,
  .p-select-option {
    background: #ffffff !important;
    background-color: #ffffff !important;
    color: #374151 !important;

    &:hover {
      background: #f3f4f6 !important;
      background-color: #f3f4f6 !important;
    }

    &.p-highlight {
      background: #e5e7eb !important;
      background-color: #e5e7eb !important;
    }
  }
}

// Force light theme on body when MSelector dropdown is open
body:has(.m-selector .p-select-overlay) {
  --p-accordion-content-background: #ffffff !important;
  --p-content-background: #ffffff !important;
  --p-surface-0: #ffffff !important;
  --p-surface-50: #ffffff !important;
  --p-surface-100: #ffffff !important;
}

// Direct targeting of any element with dark background
[style*='--p-accordion-content-background: #18181b'],
[style*='background-color: #18181b'] {
  --p-accordion-content-background: #ffffff !important;
  background-color: #ffffff !important;
}

.m-selector {
  width: 100%;
  min-height: 40px;
  box-sizing: border-box;
  align-items: center;
  background: #ffffff !important;
  background-color: #ffffff !important;
  border: 1px solid #d1d5db !important;
  border-radius: 8px !important;
  overflow: hidden;
  transition:
    border-color 0.12s ease,
    box-shadow 0.12s ease;

  &:hover:not(.m-selector--disabled):not(.m-selector--readonly) {
    border-color: #9ca3af !important;
  }

  &:focus-within:not(.m-selector--readonly) {
    border-color: var(--primary-color, #1e3a6e) !important;
    box-shadow: 0 0 0 2px color-mix(in srgb, var(--primary-color, #1e3a6e) 14%, transparent);
  }

  &--readonly,
  &--disabled {
    cursor: default !important;
    background: #f9fafb !important;
    opacity: 1 !important;

    :deep(.p-select-label),
    :deep(.p-select-label *) {
      background: #f9fafb !important;
      color: #4b5563 !important;
      cursor: default !important;
    }

    :deep(.p-select-dropdown),
    :deep(.p-select-trigger),
    :deep(.p-select-clear-icon) {
      display: none !important;
      width: 0 !important;
      border: none !important;
      padding: 0 !important;
    }
  }

  :deep(.p-select-label) {
    flex: 1 1 auto;
    min-height: 40px;
    padding: 0.5rem 0.75rem;
    font-size: 14px;
    line-height: 1.5;
    text-align: v-bind('props.align');
    box-sizing: border-box;
    display: flex;
    align-items: center;
    background: #ffffff !important;
    background-color: #ffffff !important;
    color: #374151 !important;
    border: none !important;

    &.p-placeholder {
      color: rgba(0, 0, 0, 0.38);
    }
  }

  :deep(.p-select-dropdown),
  :deep(.p-select-trigger) {
    width: 2.5rem;
    align-self: stretch;
    background: #ffffff !important;
    background-color: #ffffff !important;
    color: #6b7280 !important;
    border: none !important;
    border-left: 1px solid #eef0f3 !important;
  }

  // Additional styles to override any dark theme variables
  :deep(.p-select-overlay) {
    background: #ffffff !important;
    background-color: #ffffff !important;
    border: 1px solid #d1d5db !important;

    .p-select-panel {
      background: #ffffff !important;
      background-color: #ffffff !important;
    }

    .p-select-option {
      background: #ffffff !important;
      background-color: #ffffff !important;
      color: #374151 !important;

      &:hover {
        background: #f3f4f6 !important;
        background-color: #f3f4f6 !important;
      }

      &.p-highlight {
        background: #e5e7eb !important;
        background-color: #e5e7eb !important;
      }
    }
  }

  // Target all possible PrimeVue Select overlay classes
  :deep(.p-component-overlay),
  :deep(.p-overlay),
  :deep(.p-connected-overlay) {
    background: #ffffff !important;
    background-color: #ffffff !important;
    border: 1px solid #d1d5db !important;

    .p-select-panel,
    .p-select-list-container {
      background: #ffffff !important;
      background-color: #ffffff !important;
    }
  }

  // Override any CSS custom properties that might be setting dark backgrounds
  :deep(.p-select-overlay),
  :deep(.p-select-panel),
  :deep(.p-select-option),
  :deep(.p-select-list-container) {
    --p-accordion-content-background: #ffffff !important;
    --p-content-background: #ffffff !important;
    --p-surface-0: #ffffff !important;
    --p-surface-50: #ffffff !important;
    --p-surface-100: #ffffff !important;
    background: #ffffff !important;
    background-color: #ffffff !important;
  }

  // Additional specific targeting for list container
  :deep(.p-select-list-container) {
    background: #ffffff !important;
    background-color: #ffffff !important;
    border: 1px solid #d1d5db !important;

    .p-select-list {
      background: #ffffff !important;
      background-color: #ffffff !important;
    }

    .p-select-option {
      background: #ffffff !important;
      background-color: #ffffff !important;
      color: #374151 !important;

      &:hover {
        background: #f3f4f6 !important;
        background-color: #f3f4f6 !important;
      }

      &.p-highlight {
        background: #e5e7eb !important;
        background-color: #e5e7eb !important;
      }
    }
  }

  &--disabled {
    :deep(.p-dropdown) {
      opacity: 0.6;
    }
  }
}

.selected-values {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}

.selected-tag {
  background: #e5e7eb;
  padding: 0.125rem 0.5rem;
  border-radius: 4px;
  font-size: 12px;
}

.selector-option {
  padding: 0.5rem 0.75rem;
  font-size: 14px;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
  line-height: 1.35;
}

.m-selector-value {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  max-width: 100%;
  /* Fixed strut. Emoji option text (star ratings) otherwise sits on the
     bottom border: the font box is taller than the control and gets clipped. */
  min-height: 0;
  height: 1.25rem;
  line-height: 1.25rem;
  font-size: 14px;
}

.m-selector-value--placeholder {
  color: rgba(0, 0, 0, 0.38);
}

.m-selector--compact {
  min-height: 0 !important;

  :deep(.p-select-label) {
    min-height: 0 !important;
    padding: 0.375rem 0.5rem !important;
    line-height: 1.25;
  }

  :deep(.p-select-dropdown),
  :deep(.p-select-trigger) {
    width: 2rem;
  }

  .m-selector-value {
    height: auto;
    min-height: 0;
    line-height: 1.25;
  }
}

/* Overlay на body — глобальные правила (не scoped к wrapper) */
.m-selector-overlay.p-select-overlay,
.m-selector-overlay {
  box-sizing: border-box;
  width: var(--m-selector-overlay-anchor-width, max-content);
  min-width: var(--m-selector-overlay-anchor-width, 0);
  max-width: var(--m-selector-overlay-anchor-width, calc(100vw - 1rem));
  margin-top: 4px !important;
  padding: 0.35rem !important;
  border: 1px solid #e5e7eb !important;
  border-radius: 10px !important;
  background: #ffffff !important;
  box-shadow:
    0 12px 28px rgba(15, 23, 42, 0.12),
    0 2px 8px rgba(15, 23, 42, 0.06) !important;
  overflow: hidden;
}

.m-selector-overlay .p-select-list-container,
.m-selector-overlay .p-select-list {
  background: transparent !important;
  border: none !important;
  padding: 0 !important;
}

.m-selector-overlay .p-select-option,
.m-selector-overlay .selector-option {
  white-space: normal !important;
  overflow-wrap: anywhere;
  word-break: break-word;
  line-height: 1.35;
  align-items: flex-start;
}

.m-selector-overlay .p-select-option {
  margin: 0.1rem 0 !important;
  padding: 0.5rem 0.75rem !important;
  border-radius: 8px !important;
  color: #334155 !important;
  background: transparent !important;
  transition: background-color 0.1s ease, color 0.1s ease;
}

.m-selector-overlay .p-select-option:not(.p-disabled):hover,
.m-selector-overlay .p-select-option:not(.p-disabled).p-focus {
  background: #f1f5f9 !important;
  color: #0f172a !important;
}

.m-selector-overlay .p-select-option.p-highlight,
.m-selector-overlay .p-select-option[data-p-selected='true'] {
  background: color-mix(in srgb, var(--primary-color, #1e3a6e) 12%, white) !important;
  color: #0f172a !important;
  font-weight: 500;
}

.m-selector-overlay .selector-option {
  padding: 0;
}

.m-selector-overlay--compact .p-select-option {
  padding: 0.375rem 0.625rem !important;
}

.m-selector-overlay--compact .selector-option {
  font-size: 13px;
}

.m-selector-hint {
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 0.25rem;
  padding: 0 0.5rem;
}

// Выравнивание текста
.m-selector.text-align-center {
  :deep(.p-dropdown-label) {
    text-align: center;
  }
}

.m-selector.text-align-right {
  :deep(.p-dropdown-label) {
    text-align: right;
  }
}
</style>
